import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Badge,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar, dashboardData } = useApp();

  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path.startsWith('/leads')) return 'Leads';
    if (path.startsWith('/clients')) return 'Clients';
    if (path.startsWith('/billing')) return 'Billing';
    if (path.startsWith('/analytics')) return 'Analytics';
    if (path.startsWith('/templates')) return 'DM Templates';
    if (path.startsWith('/settings')) return 'Settings';
    if (path.startsWith('/warmup')) return 'Warm Up Mode';
    return 'WebFlow CRM';
  };

  // Get notification count
  const overdueCount = dashboardData?.followUps?.overdue || 0;
  const invoicesDue = dashboardData?.billing?.invoicesDueToday || 0;
  const totalNotifications = overdueCount + invoicesDue;

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/leads?search=${encodeURIComponent(searchValue)}`);
      setSearchValue('');
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            edge="start"
            onClick={toggleSidebar}
            sx={{ color: 'text.secondary' }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Page title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            flexGrow: { xs: 1, md: 0 },
          }}
        >
          {getPageTitle()}
        </Typography>

        {/* Search bar */}
        {!isMobile && (
          <TextField
            placeholder="Search leads..."
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleSearch}
            sx={{
              width: 300,
              ml: 4,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.50',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Add Lead Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/leads/new')}
          sx={{
            display: { xs: 'none', sm: 'flex' },
          }}
        >
          Add Lead
        </Button>

        {/* Mobile Add Button */}
        {isMobile && (
          <IconButton
            color="primary"
            onClick={() => navigate('/leads/new')}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <AddIcon />
          </IconButton>
        )}

        {/* Notifications */}
        <IconButton
          onClick={handleNotificationClick}
          sx={{ color: 'text.secondary' }}
        >
          <Badge badgeContent={totalNotifications} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>

        {/* Notification Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { width: 320, maxHeight: 400 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
            </Typography>
          </Box>
          <Divider />

          {overdueCount > 0 && (
            <MenuItem
              onClick={() => {
                navigate('/leads?hasOverdueFollowup=true');
                handleNotificationClose();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'warning.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <WarningIcon sx={{ color: 'warning.dark' }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {overdueCount} Overdue Follow-ups
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Leads need attention
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          )}

          {invoicesDue > 0 && (
            <MenuItem
              onClick={() => {
                navigate('/billing?status=due');
                handleNotificationClose();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'info.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ReceiptIcon sx={{ color: 'info.dark' }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {invoicesDue} Invoices Due Today
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Require payment
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          )}

          {totalNotifications === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No new notifications
              </Typography>
            </Box>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
